import Badge from "@cloudscape-design/components/badge"
import Box from "@cloudscape-design/components/box"
import Button from "@cloudscape-design/components/button"
import Container from "@cloudscape-design/components/container"
import Header from "@cloudscape-design/components/header"
import SpaceBetween from "@cloudscape-design/components/space-between"
import Table from "@cloudscape-design/components/table"
import { useState } from "react"
import type { DTADetails } from "../../../services/dtaApi"
import type { DTAVersion } from "../../../types/dta"
import Pagination from "../../../components/Pagination/Pagination"
import { stageBadgeColor } from "../../../components/shared/dtaDisplay"
import { formatUserId } from "../../../utils/userFormatter"
import ActivityTimeline from "./ActivityTimeline"

const ITEMS_PER_PAGE = 20

interface AuditTrailPanelProps {
  /**
   * The DTA's status-transition history, shown as the top Activity timeline.
   */
  statusHistory: DTADetails["status_history"]
  /**
   * All versions of the DTA, shown in the Versions table.
   */
  versions: DTAVersion[]
  selectedVersion: string
  onVersionChange: (version: string) => void
}

/**
 * Audit Trail tab: the full record of what happened to a DTA. Top section is the Activity timeline
 * (stage changes shown as a stage pill, in-stage edits/approvals with a grey dot); below it a
 * Versions section lists each version with its stage, creator, date, and change reason, plus a
 * switch-to-version action.
 */
export default function AuditTrailPanel({
  statusHistory,
  versions,
  selectedVersion,
  onVersionChange,
}: AuditTrailPanelProps) {
  const [currentPage, setCurrentPage] = useState(1)

  const pageCount = Math.max(1, Math.ceil(versions.length / ITEMS_PER_PAGE))
  // Clamp so the table never blanks out if the version list shrinks under us.
  const page = Math.min(currentPage, pageCount)
  const paginatedVersions = versions.slice((page - 1) * ITEMS_PER_PAGE, page * ITEMS_PER_PAGE)

  return (
    <SpaceBetween size="l">
      <Container header={<Header variant="h2">Activity</Header>}>
        <ActivityTimeline statusHistory={statusHistory} ariaLabel="DTA activity" />
      </Container>

      <Container header={<Header variant="h2">Versions</Header>}>
        <Table
          variant="embedded"
          items={paginatedVersions}
          empty={
            <Box textAlign="center" padding="l">
              No versions available.
            </Box>
          }
          pagination={
            versions.length > ITEMS_PER_PAGE ? (
              <Pagination
                currentPage={page}
                pageCount={pageCount}
                onNext={() => setCurrentPage(Math.min(pageCount, page + 1))}
                onPrevious={() => setCurrentPage(Math.max(1, page - 1))}
                onPageChange={setCurrentPage}
              />
            ) : undefined
          }
          columnDefinitions={[
            {
              id: "version",
              header: "Version",
              cell: (v) => <Box fontWeight="bold">{v.version}</Box>,
            },
            {
              id: "stage",
              header: "Stage",
              cell: (v) => (
                <Badge color={stageBadgeColor(v.workflow_stage || v.status)}>
                  {v.workflow_stage || v.status}
                </Badge>
              ),
            },
            {
              id: "created",
              header: "Created",
              cell: (v) => (
                <Box color="text-status-inactive">
                  {new Date(v.created_at).toLocaleDateString()}
                </Box>
              ),
            },
            {
              id: "by",
              header: "By",
              cell: (v) => <Box color="text-status-inactive">{formatUserId(v.created_by)}</Box>,
            },
            {
              id: "reason",
              header: "Reason",
              cell: (v) => v.change_reason || "-",
            },
            {
              id: "actions",
              header: "Actions",
              cell: (v) => (
                <Button
                  variant="inline-link"
                  onClick={() => onVersionChange(v.version)}
                  disabled={v.version === selectedVersion}
                >
                  {v.version === selectedVersion ? "Current" : "View"}
                </Button>
              ),
            },
          ]}
        />
      </Container>
    </SpaceBetween>
  )
}
